import { AlertTitle } from "@mui/material"
import { ReactElement } from "react"

import { AddApplicationComment } from "./addApplicationComment"
import useAddApplicationComment from "../../../services/middleware/hooks/application/useAddApplicationComment"
import useDeleteApplicationComment from "../../../services/middleware/hooks/application/useDeleteApplicationComment"
import useReadApplicationComment from "../../../services/middleware/hooks/application/useReadApplicationComment"
import { IComment } from "../../../services/middleware/mappers/singleDeviceMapper"
import { IApplicationComment } from "../../../services/middleware/types/installerInterface/installerApplicationTypes"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import { AppCard, AppCardHeader } from "../../surfaces/appCard"
import { GenericCommentsCard } from "../genericComments/genericCommentsCard"

interface IApplicationCommentsCardProps {
    applicationId: string
    comments: IApplicationComment[]
    mutate: () => void
    fetchingComments: boolean
    isDno: boolean
    title: string
    subheader: string
    isRedacted?: boolean
    commentStatus?: boolean
    lastReadBy?: string
    lastReadAt?: string
}

export const ApplicationCommentsCard = ({
    applicationId,
    comments,
    mutate,
    fetchingComments,
    isDno,
    title,
    subheader,
    isRedacted = false,
    commentStatus = false,
    lastReadBy,
    lastReadAt,
}: IApplicationCommentsCardProps): ReactElement => {
    const {
        isSending: addingComment,
        error: addError,
        postResponse: addComment,
    } = useAddApplicationComment()

    const {
        isSending: deletingComment,
        error: deleteError,
        postResponse: deleteComment,
    } = useDeleteApplicationComment()

    const {
        isSending: readingComment,
        error: readError,
        patchResponse: readComment,
    } = useReadApplicationComment()

    if (isRedacted) {
        return (
            <AppCard>
                <AppCardHeader
                    title={title}
                    subheader={subheader}
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                />
                <AppAlert severity="warning">
                    <AlertTitle>This application has been redacted</AlertTitle>
                    Comments on this application are no longer visible
                </AppAlert>
            </AppCard>
        )
    }

    const removeComment = (commentId: string): Promise<void> =>
        deleteComment(applicationId, commentId, isDno)

    const renderCreateComponent = (isLoading: boolean): ReactElement => (
        <AddApplicationComment
            applicationId={applicationId}
            mutate={mutate}
            isLoading={isLoading}
            isDno={isDno}
            commentStatus={commentStatus}
            createComment={addComment}
            readComment={readComment}
            lastReadBy={lastReadBy}
            lastReadAt={lastReadAt}
        />
    )

    const canDeleteComment = (): boolean => false

    const formattedComments: IComment[] = comments.map((comment) => ({
        id: comment.commentId,
        comment: comment.comment,
        firstName: comment.firstname,
        lastName: comment.lastname,
        initials: comment.initials,
        fromRequestor: comment.fromRequestor,
        publishedAt: comment.publishedAt,
        groupType: null,
    }))

    return (
        <GenericCommentsCard
            title={title}
            subheader={subheader}
            fetchingComments={fetchingComments}
            deleteError={deleteError}
            deleteLoading={deletingComment}
            postDelete={removeComment}
            createError={addError}
            createLoading={addingComment}
            canCreateComments
            canViewComments
            canDeleteComment={canDeleteComment}
            mutate={mutate}
            comments={formattedComments}
            renderCreateComponent={renderCreateComponent}
        />
    )
}

ApplicationCommentsCard.defaultProps = {
    isRedacted: false,
    commentStatus: false,
    lastReadBy: undefined,
    lastReadAt: undefined,
}
